import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../Header";
import { Table, Input, Form } from "antd";
import { FetchAllCourses, UpdateCourse } from "../../api/aws-api"; // Make sure UpdateCourse URL is correct
import { ConstructorEventGet, ConstructorEventPost } from "../../Event/event";
import { ClassicSpinner } from "react-spinners-kit";

const CourseManagement = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState(""); // currently editing row
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = data.filter((item) => {
    const searchValue = searchText.toLowerCase();

    return (
      item.course_name?.toLowerCase().includes(searchValue) ||
      item.course_catogory?.toLowerCase().includes(searchValue) ||
      item.duration?.toString().toLowerCase().includes(searchValue)
    );
  });
  const fetchData = async () => {
    setLoading(true);
    const response = await ConstructorEventGet(FetchAllCourses);
    // Add unique key for table
    setData(response.map((item) => ({ ...item, key: item.course_id })));
    setLoading(false);
  };

  const isEditing = (record) => record.key === editingKey;

  const edit = (record) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const record = data.find((item) => item.key === key);

      // Create payload for backend
      const payload = {
        course_id: record.course_id, // Use course_id as primary key
        course_name: row.course_name,
        course_catogory: row.course_catogory,
        duration: row.duration,
      };

      setLoading(true);

      // Call the UpdateCourse Lambda
      await ConstructorEventPost(UpdateCourse, { body: JSON.stringify(payload) });

      // Re-fetch data from backend to reflect changes
      await fetchData();
      setEditingKey("");
      setLoading(false);
    } catch (err) {
      console.error("Update failed:", err);
      setLoading(false);
    }
  };

  const EditableCell = ({ editing, dataIndex, title, record, children, ...restProps }) => {
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: true, message: `Please input ${title}!` }]}
          >
            <Input
              onPressEnter={() => save(record.key)} // Save on Enter key
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    { title: "Course Name", dataIndex: "course_name", editable: true },
    { title: "Course Category", dataIndex: "course_catogory", editable: true },
    { title: "Duration", dataIndex: "duration", editable: true },
    {
      title: "Last Updated",
      dataIndex: "last_updated",
      editable: false,
      render: (text) => {
        if (!text) return "";
        const date = new Date(text);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        }); // Example: 28 March, 2023
      },
    },
    {
      title: "Actions",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <a onClick={() => save(record.key)} style={{ marginRight: 8 }}>
              Save
            </a>
            <a onClick={cancel}>Cancel</a>
          </span>
        ) : (
          <div style={{ display: "flex", alignItems: "center" }}>
            {/* Edit Icon */}
            <iconify-icon
              icon="ri:edit-fill"
              style={{ color: "red", fontSize: "1.8em", cursor: "pointer" }}
              onClick={() => edit(record)}
            />
            {/* Activity Icon */}
            <Link
              to="/codomo/admin/activitymanagement"
              state={{ data: record }}
            >
              <iconify-icon
                icon="icon-park-outline:activity-source"
                style={{ color: "blue", fontSize: "1.8em", marginLeft: "20px" }}
              ></iconify-icon>
            </Link>
            {/* Download Icon */}
            <Link
              to="/codomo/admin/downloadsmanagement"
              state={{ data: record }}
            >
              <iconify-icon
                icon="material-symbols:download"
                style={{ color: "green", fontSize: "1.8em", marginLeft: "20px" }}
              ></iconify-icon>
            </Link>
            {/* Mapping Icon */}
            <Link
              to="/codomo/admin/mappingmanagement"
              state={{ data: record }}
            >
              <iconify-icon
                icon="ic:outline-map"
                style={{ color: "#5B235C", fontSize: "1.8em", marginLeft: "20px" }}
              ></iconify-icon>
            </Link>
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <>
      <Header />
      {loading ? (
        <div className="spinner">
          <div className="spinner-wrapper">
            <ClassicSpinner size={50} color="black" loading={loading} />
          </div>
        </div>
      ) : (
        <div className="nav-tab-wrapper tabs section-padding">
          <div className="container">
            <div className="mini-title">
              Course Management
              <div className="small-btn ml-4" onClick={() => navigate("/codomo/admin/createnewcourse")}>
                New Course <iconify-icon icon="pajamas:doc-new"></iconify-icon>
              </div>
            </div>
            <div className="col-span-12">
              <div className="bg-white shadow-box7 p-8 rounded-md">
                <Form form={form} component={false}>
                  <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end", }}>
                    <Input
                      placeholder="Search courses..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                  </div>
                  <Table
                    components={{ body: { cell: EditableCell } }}
                    bordered
                    dataSource={filteredData}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    pagination={data.length > 9}
                    rowKey="key"
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CourseManagement;

































// import React, { useState, useEffect } from "react";
// import { useNavigate, Link } from "react-router-dom";
// import Header from "../Header";
// import { Table, Input, Form } from "antd";
// import { FetchAllCourses, UpdateCourse } from "../../api/aws-api"; // Make sure UpdateCourse URL is correct
// import { ConstructorEventGet, ConstructorEventPost } from "../../Event/event";
// import { ClassicSpinner } from "react-spinners-kit";

// const CourseManagement = () => {
//   const [loading, setLoading] = useState(false);
//   const [data, setData] = useState([]);
//   const [editingKey, setEditingKey] = useState(""); // currently editing row
//   const [form] = Form.useForm();
//   const [searchText, setSearchText] = useState("");
//   const navigate = useNavigate();

//   useEffect(() => {
//     fetchData();
//   }, []);

//   const filteredData = data.filter((item) => {
//     const searchValue = searchText.toLowerCase();

//     return (
//       item.course_name?.toLowerCase().includes(searchValue) ||
//       item.course_catogory?.toLowerCase().includes(searchValue) ||
//       item.duration?.toString().toLowerCase().includes(searchValue)
//     );
//   });
//   const fetchData = async () => {
//     setLoading(true);
//     const response = await ConstructorEventGet(FetchAllCourses);
//     // Add unique key for table
//     setData(response.map((item) => ({ ...item, key: item.course_id })));
//     setLoading(false);
//   };

//   const isEditing = (record) => record.key === editingKey;

//   const edit = (record) => {
//     form.setFieldsValue({ ...record });
//     setEditingKey(record.key);
//   };

//   const cancel = () => {
//     setEditingKey("");
//   };

//   const save = async (key) => {
//     try {
//       const row = await form.validateFields();
//       const record = data.find((item) => item.key === key);

//       // Create payload for backend
//       const payload = {
//         course_id: record.course_id, // Use course_id as primary key
//         course_name: row.course_name,
//         course_catogory: row.course_catogory,
//         duration: row.duration,
//       };

//       setLoading(true);

//       // Call the UpdateCourse Lambda
//       await ConstructorEventPost(UpdateCourse, { body: JSON.stringify(payload) });

//       // Re-fetch data from backend to reflect changes
//       await fetchData();
//       setEditingKey("");
//       setLoading(false);
//     } catch (err) {
//       console.error("Update failed:", err);
//       setLoading(false);
//     }
//   };

//   const EditableCell = ({ editing, dataIndex, title, record, children, ...restProps }) => {
//     return (
//       <td {...restProps}>
//         {editing ? (
//           <Form.Item
//             name={dataIndex}
//             style={{ margin: 0 }}
//             rules={[{ required: true, message: `Please input ${title}!` }]}
//           >
//             <Input
//               onPressEnter={() => save(record.key)} // Save on Enter key
//             />
//           </Form.Item>
//         ) : (
//           children
//         )}
//       </td>
//     );
//   };

//   const columns = [
//     { title: "Course Name", dataIndex: "course_name", editable: true },
//     { title: "Course Category", dataIndex: "course_catogory", editable: true },
//     { title: "Duration", dataIndex: "duration", editable: true },
//     {
//       title: "Last Updated",
//       dataIndex: "last_updated",
//       editable: false,
//       render: (text) => {
//         if (!text) return "";
//         const date = new Date(text);
//         return date.toLocaleDateString("en-GB", {
//           day: "2-digit",
//           month: "long",
//           year: "numeric",
//         }); // Example: 28 March, 2023
//       },
//     },
//     {
//       title: "Actions",
//       render: (_, record) => {
//         const editable = isEditing(record);
//         return editable ? (
//           <span>
//             <a onClick={() => save(record.key)} style={{ marginRight: 8 }}>
//               Save
//             </a>
//             <a onClick={cancel}>Cancel</a>
//           </span>
//         ) : (
//           <div style={{ display: "flex", alignItems: "center" }}>
//             {/* Edit Icon */}
//             <iconify-icon
//               icon="ri:edit-fill"
//               style={{ color: "red", fontSize: "1.8em", cursor: "pointer" }}
//               onClick={() => edit(record)}
//             />
//             {/* Activity Icon */}
//             <Link
//               to="/codomo/admin/activitymanagement"
//               state={{ data: record }}
//             >
//               <iconify-icon
//                 icon="icon-park-outline:activity-source"
//                 style={{ color: "blue", fontSize: "1.8em", marginLeft: "20px" }}
//               ></iconify-icon>
//             </Link>
//             {/* Download Icon */}
//             <Link
//               to="/codomo/admin/downloadsmanagement"
//               state={{ data: record }}
//             >
//               <iconify-icon
//                 icon="material-symbols:download"
//                 style={{ color: "green", fontSize: "1.8em", marginLeft: "20px" }}
//               ></iconify-icon>
//             </Link>
//             {/* Mapping Icon */}
//             <Link
//               to="/codomo/admin/mappingmanagement"
//               state={{ data: record }}
//             >
//               <iconify-icon
//                 icon="ic:outline-map"
//                 style={{ color: "#5B235C", fontSize: "1.8em", marginLeft: "20px" }}
//               ></iconify-icon>
//             </Link>
//           </div>
//         );
//       },
//     },
//   ];

//   const mergedColumns = columns.map((col) => {
//     if (!col.editable) return col;
//     return {
//       ...col,
//       onCell: (record) => ({
//         record,
//         dataIndex: col.dataIndex,
//         title: col.title,
//         editing: isEditing(record),
//       }),
//     };
//   });

//   return (
//     <>
//       <Header />
//       {loading ? (
//         <div className="spinner">
//           <div className="spinner-wrapper">
//             <ClassicSpinner size={50} color="black" loading={loading} />
//           </div>
//         </div>
//       ) : (
//         <div className="nav-tab-wrapper tabs section-padding">
//           <div className="container">
//             <div className="mini-title">
//               Course Management
//               <div className="small-btn ml-4" onClick={() => navigate("/codomo/admin/createnewcourse")}>
//                 New Course <iconify-icon icon="pajamas:doc-new"></iconify-icon>
//               </div>
//             </div>
//             <div className="col-span-12">
//               <div className="bg-white shadow-box7 p-8 rounded-md">
//                 <Form form={form} component={false}>
//                   <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end", }}>
//                     <Input
//                       placeholder="Search courses..."
//                       value={searchText}
//                       onChange={(e) => setSearchText(e.target.value)}
//                       style={{ width: 300 }}
//                       allowClear
//                     />
//                   </div>
//                   <Table
//                     components={{ body: { cell: EditableCell } }}
//                     bordered
//                     dataSource={filteredData}
//                     columns={mergedColumns}
//                     rowClassName="editable-row"
//                     pagination={data.length > 9}
//                     rowKey="key"
//                   />
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default CourseManagement;



























































